import { type ReactNode } from 'react';

import { Box, Checkbox, IconButton, Typography } from '@mui/material';

import { ItemRow } from '../item-row';
import type { SearchPanelGroup } from '../types';

import { getGroupState } from './get-group-state';
import { GroupHeaderStyled } from './group-row.styled';

type GroupRowProps = {
  group: SearchPanelGroup;
  meta: ReactNode; // resolved meta (the panel already applied any count function)
  selectedIds: Set<string>;
  onSelectedIdsChange: (next: Set<string>) => void;
};

export const GroupRow = (props: GroupRowProps) => {
  const { group, meta, selectedIds, onSelectedIdsChange } = props;

  const itemIds = group.items.map((item) => item.id);
  const state = getGroupState(itemIds, selectedIds);

  const toggleItem = (itemId: string) => {
    const next = new Set(selectedIds);
    if (next.has(itemId)) next.delete(itemId);
    else next.add(itemId);
    onSelectedIdsChange(next);
  };

  // Toggling the group selects all its visible items, or clears them if all are
  // already selected.
  const toggleGroup = () => {
    const next = new Set(selectedIds);
    const allSelected = itemIds.length > 0 && itemIds.every((id) => next.has(id));
    itemIds.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
    onSelectedIdsChange(next);
  };

  return (
    <Box>
      <GroupHeaderStyled onClick={toggleGroup}>
        <Checkbox
          size="small"
          checked={state === 'checked'}
          indeterminate={state === 'indeterminate'}
          readOnly
          sx={{ pointerEvents: 'none' }}
        />
        {group.icon && (
          <IconButton variant="pill" size="small" component="span">
            {group.icon}
          </IconButton>
        )}
        <Typography variant="body2" fontWeight={600} noWrap>
          {group.label}
        </Typography>
        {group.secondaryLabel && (
          <Typography variant="body2" color="text.secondary" noWrap>
            {group.secondaryLabel}
          </Typography>
        )}
        <Box flex={1} />
        {meta && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {meta}
          </Typography>
        )}
      </GroupHeaderStyled>

      {group.items.map((item, index) => (
        <ItemRow
          key={item.id}
          item={item}
          selected={selectedIds.has(item.id)}
          isLast={index === group.items.length - 1}
          onToggle={() => toggleItem(item.id)}
        />
      ))}
    </Box>
  );
};
